import React, { useState } from 'react'
import { IoIosInformationCircleOutline } from 'react-icons/io'
import { FiShoppingCart, FiDownload, FiStar } from 'react-icons/fi'
import { useDispatch } from 'react-redux'
import { Timeline, Drawer, Tabs, Tag } from 'antd'
import parse from 'html-react-parser'
import TimeAgo from 'react-timeago'
import ptBrStrings from 'react-timeago/lib/language-strings/pt-br'
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter'
import { motion } from 'framer-motion'
import { GrShare } from 'react-icons/gr'
import { FiMaximize, FiMinimize } from 'react-icons/fi'

import { 
  Container, 
  ImageWrapper, 
  Content, 
  CategoryName, 
  Title, 
  Price, 
  ButtonsContainer, 
  ActionButton, 
  DetailsHeader, 
  DetailsContent,
  DrawerStyles,
  FullscreenButton
} from './styles'

import { addProduct } from '../../store/actions/cart.actions'
import { getPluginDownloadUrl } from '../../utils/api'

function Plugin({ data }) {
  const [visible, setVisible] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const dispatch = useDispatch()

  function addToCart() {
    dispatch(
      addProduct({
        id: data.id,
        name: data.title,
        promotional: data.promotional,
        price: parseFloat(data.promotional) > 0 ? parseFloat(data.promotional) : parseFloat(data.price),
        image: data.image,
        licenses: [{ ip: '127.0.0.0', port: '25565' }],
      })
    )
  }

  function download() {
    window.open(getPluginDownloadUrl(data.id), 'blank')
  }

  function toggleFullscreen() {
    setIsFullscreen(!isFullscreen)
  }

  return (
    <Container 
      as={motion.div}
      whileHover={{ 
        y: -10,
        boxShadow: '0 20px 30px rgba(0, 0, 0, 0.1)'
      }}
      transition={{ 
        type: "spring", 
        stiffness: 300 
      }}
    >
      <DrawerStyles />
      <ImageWrapper>
        <motion.img 
          src={data.image} 
          alt={`Atlas Plugins - ${data.title}`}
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 300 }}
        />
      </ImageWrapper>
      <Content>
        <CategoryName>
          {data.category.name}{' '}
          {data.title === 'AtlasSMS' && (
            <Tag color="#0062cc">
              Exclusivo
            </Tag>
          )}
        </CategoryName>
        <Title>{data.title}</Title>
        
        {data.category.type === 'PAID' ? (
          data.promotional > 0 ? (
            <Price>
              <motion.span 
                className="old-price"
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 0.6 }}
                transition={{ yoyo: Infinity, duration: 2 }}
              >
                R$ {parseFloat(data.price).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </motion.span>
              <motion.span 
                className="current-price"
                initial={{ scale: 1 }}
                animate={{ scale: 1.05 }}
                transition={{ yoyo: Infinity, duration: 2 }}
              >
                R$ {parseFloat(data.promotional).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </motion.span>
            </Price>
          ) : (
            <Price>
              <span className="current-price">
                R$ {parseFloat(data.price).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </Price>
          )
        ) : (
          <Price>
            <span className="free-price">Gratuito</span>
          </Price>
        )}
        
        <ButtonsContainer>
          {data.category.type === 'PAID' ? (
            <ActionButton 
              primary
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={addToCart}
            >
              <FiShoppingCart /> Comprar
            </ActionButton>
          ) : (
            <ActionButton 
              primary
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={download}
            >
              <FiDownload /> Download
            </ActionButton>
          )}
          <ActionButton 
            secondary
            as={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setVisible(true)}
          >
            <IoIosInformationCircleOutline />
          </ActionButton>
        </ButtonsContainer>
      </Content>
      
      <Drawer
        title={null}
        placement="right"
        width={isFullscreen ? '100%' : 650}
        closable={true}
        onClose={() => {
          setVisible(false)
          setIsFullscreen(false)
        }}
        visible={visible}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: 'none' }}
        className={`plugin-details-drawer ${isFullscreen ? 'fullscreen' : ''}`}
      >
        <DetailsHeader className={isFullscreen ? 'fullscreen' : ''}>
          <img src={data.image} alt={`Atlas Plugins - ${data.title}`} />
          <DetailsContent>
            <h3>
              {data.title} <small>{data.version}</small>
              <GrShare
                className="share-icon"
                onClick={async () => {
                  await navigator.clipboard.writeText(`https://www.atlasplugins.com/plugins/${data.title}`)
                }}
              />
            </h3>
            <h5>{data.category.name}</h5>
            
            {data.category.type === 'PAID' && (
              <h4 className="plugin-price">
                {parseFloat(data.promotional) > 0 ? (
                  <>
                    R$ {parseFloat(data.promotional).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    <small>
                      R$ {parseFloat(data.price).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </small>
                  </>
                ) : (
                  <>
                    R$ {parseFloat(data.price).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </>
                )}
              </h4>
            )}
            
            {data.category.type === 'PAID' ? (
              <ActionButton
                primary
                fullWidth
                onClick={() => {
                  addToCart()
                  setVisible(false)
                  setIsFullscreen(false)
                }}
              >
                <FiShoppingCart /> Comprar Agora
              </ActionButton>
            ) : (
              <ActionButton primary fullWidth onClick={download}>
                <FiDownload /> Download
              </ActionButton>
            )}
            
            <div className="plugin-stats">
              <FiStar /> <span>{data.downloads}</span> cópias vendidas
            </div>
          </DetailsContent>
          
          <FullscreenButton onClick={toggleFullscreen}>
            {isFullscreen ? <FiMinimize /> : <FiMaximize />}
          </FullscreenButton>
        </DetailsHeader>
        
        <Tabs defaultActiveKey="1" className="plugin-tabs">
          <Tabs.TabPane tab="Visão geral" key="1">
            <div className="tab-content">
              {data.description ? parse(data.description) : ''}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Comandos" key="2">
            <div className="tab-content">
              {data.commands ? parse(data.commands) : ''}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Permissões" key="3">
            <div className="tab-content">
              {data.permissions ? parse(data.permissions) : ''}
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Atualizações" key="4">
            <div className="tab-content updates">
              {data.updates && data.updates.length > 0 ? (
                <Timeline>
                  {data.updates.map((el) => (
                    <Timeline.Item color="#0062cc" key={el.id}>
                      <h4>
                        <span className="version">v{el.version}</span> 
                        <span className="date">
                          <TimeAgo
                            date={el.updated_at}
                            formatter={buildFormatter(ptBrStrings)}
                          />
                        </span>
                      </h4>
                      <div className="update-content">
                        {el.description ? parse(el.description) : ''}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <div className="no-updates">
                  Nenhuma atualização disponível para este plugin.
                </div>
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Drawer>
    </Container>
  )
}

export default Plugin
